"use client";

import { useEffect, useRef, useState, type DragEvent } from "react";
import Link from "next/link";
import { ChevronIcon, FolderIcon, ReportIcon } from "@/components/icons";
import { useConfirm } from "@/components/ConfirmDialog";
import { requestJson } from "@/lib/clientRequest";
import { readBusinessCookie } from "@/lib/business/businessCookie";
import { buildFolderTree, groupWorkersByFolder } from "./folderTreeModel";
import { SidebarSectionHeader } from "./SidebarSectionHeader";
import { useFolderExpansion } from "./useFolderExpansion";
import { useWorkerMoving } from "./useWorkerMoving";
import { WorkerRow } from "./WorkerRow";
import type { FolderNode, SidebarFolder, SidebarWorker } from "./types";

const DRAG_TYPE = "application/x-caw-monitoring-folder";
const actionClass = "cursor-pointer rounded px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 focus-visible:outline-2 focus-visible:outline-violet-400 disabled:cursor-default disabled:opacity-40";
const dropClass = "rounded-lg data-[drop-active=true]:bg-violet-500/20 data-[drop-active=true]:ring-1 data-[drop-active=true]:ring-violet-400";
type Draft = { id?: string; name: string; parentId: string | null };

/** Edits the same worker folders used by Automation. Mounted with a business key. */
export function MonitoringFolderTree({ businessId, folders, workers = [], pathname = "/monitoring", selectedId, hrefFor, onCloseDrawer, onChanged, onDeleted }: {
  businessId: string;
  folders: SidebarFolder[];
  workers?: SidebarWorker[];
  pathname?: string;
  selectedId: string | null;
  hrefFor: (id: string | null) => string;
  onCloseDrawer: () => void;
  onChanged?: () => Promise<void>;
  onDeleted?: (folder: SidebarFolder) => void;
}) {
  const ask = useConfirm();
  const expansion = useFolderExpansion();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState<string | null | undefined>();
  const mounted = useRef(true);
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; inFlight.current?.abort(); };
  }, []);
  const currentScope = () => mounted.current && (!readBusinessCookie() || readBusinessCookie() === businessId);
  const scopedFolders = folders.filter(folder => folder.businessId === businessId);
  const tree = buildFolderTree(scopedFolders);
  const scopedWorkers = workers.filter(worker => worker.businessId === businessId);
  const workersByFolder = groupWorkersByFolder(scopedWorkers, scopedFolders);
  const rootWorkers = workersByFolder.get("") ?? [];
  const movement = useWorkerMoving({ workers: scopedWorkers, folders: scopedFolders, onMoved: onChanged, expand: expansion.expand });
  const workerRow = (worker: SidebarWorker, depth: number) => <WorkerRow key={worker.id} worker={worker} depth={depth} view="monitoring"
    hrefOverride={`/workers/${worker.id}?tab=runs`} pathname={pathname} onCloseDrawer={onCloseDrawer} movement={movement} />;
  const choices: { id: string; label: string }[] = [];
  function addChoices(nodes: FolderNode[], path = "") {
    for (const node of nodes) {
      const label = path ? `${path} / ${node.name}` : node.name;
      choices.push({ id: node.id, label });
      addChoices(node.children, label);
    }
  }
  addChoices(tree);
  function containsFolder(ancestor: string, id: string) {
    const seen = new Set<string>();
    let current: string | null = id;
    while (current && !seen.has(current)) {
      if (current === ancestor) return true;
      seen.add(current);
      current = scopedFolders.find(folder => folder.id === current)?.parentId ?? null;
    }
    return false;
  }
  async function mutate(method: string, id?: string, body?: object, afterSave?: () => void) {
    if (!businessId || !currentScope() || inFlight.current) return false;
    const controller = new AbortController();
    inFlight.current = controller;
    setPending(true); setError(null);
    try {
      await requestJson(id ? `/api/folders/${encodeURIComponent(id)}` : "/api/folders", {
        method, ...(body ? { body } : {}), signal: controller.signal, fallback: "Could not update folder.",
      });
      if (!currentScope() || controller.signal.aborted) return false;
      afterSave?.();
      window.dispatchEvent(new Event("caw:activity-refresh"));
      await onChanged?.();
      return currentScope() && !controller.signal.aborted;
    } catch (cause) {
      if (currentScope() && !controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not update folder.");
      return false;
    } finally {
      if (inFlight.current === controller) inFlight.current = null;
      if (currentScope()) setPending(false);
    }
  }
  function startDraft(next: Draft) {
    setDraft(next); setMenu(null); setError(null);
    if (next.parentId) expansion.expand(next.parentId);
  }
  async function saveDraft() {
    if (!draft?.name.trim()) return;
    const body = draft.id ? { name: draft.name.trim() } : { name: draft.name.trim(), parentId: draft.parentId, businessId };
    await mutate(draft.id ? "PATCH" : "POST", draft.id, body, () => setDraft(null));
  }
  async function remove(folder: SidebarFolder) {
    if (!await ask({ title: `Delete folder “${folder.name}”?`, message: "Its subfolders and workers move up a level. This also updates Automation.", confirmLabel: "Delete folder" })) return;
    await mutate("DELETE", folder.id, undefined, () => { setMenu(null); onDeleted?.(folder); });
  }
  async function move(id: string, parentId: string | null) {
    setOver(undefined);
    const folder = scopedFolders.find(item => item.id === id);
    if (!folder || folder.parentId === parentId || (parentId && (!scopedFolders.some(item => item.id === parentId) || containsFolder(id, parentId)))) return;
    await mutate("PATCH", id, { parentId }, () => { setMenu(null); if (parentId) expansion.expand(parentId); });
  }
  const dragProps = (id: string) => ({
    draggable: !pending,
    onDragStart: (event: DragEvent) => {
      event.stopPropagation();
      event.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ businessId, id }));
      event.dataTransfer.effectAllowed = "move";
    },
    onDragEnd: () => setOver(undefined),
  });
  const accepts = (event: DragEvent) => !pending && Array.from(event.dataTransfer.types).includes(DRAG_TYPE);
  function handleDragOver(event: DragEvent, id: string | null) {
    if (!accepts(event)) return;
    event.preventDefault(); event.stopPropagation();
    event.dataTransfer.dropEffect = "move"; setOver(id);
  }
  function handleDragLeave(event: DragEvent) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(undefined);
  }
  function handleDrop(event: DragEvent, id: string | null) {
    if (!accepts(event)) return;
    event.preventDefault(); event.stopPropagation(); setOver(undefined);
    try {
      const payload = JSON.parse(event.dataTransfer.getData(DRAG_TYPE));
      if (payload.businessId === businessId && typeof payload.id === "string") void move(payload.id, id);
    } catch { /* Ignore malformed or unrelated drags. */ }
  }
  function folderRow(folder: FolderNode, depth: number) {
    const open = expansion.isExpanded(folder.id);
    const active = selectedId === folder.id;
    return <li key={folder.id} data-monitoring-folder={folder.id}>
      <div {...movement.folderProps(folder.id)} className={dropClass}>
        <div data-monitoring-folder-drop={folder.id} data-drop-active={over === folder.id}
          onDragOver={event => handleDragOver(event, folder.id)} onDragLeave={handleDragLeave} onDrop={event => handleDrop(event, folder.id)} style={{ paddingLeft: 4 + depth * 14 }} className={`group flex items-center gap-1 pr-1 text-sm ${dropClass} ${active ? "bg-violet-500/10 font-medium text-violet-300" : "text-zinc-300 hover:bg-zinc-800/60"}`}>
          <button type="button" aria-label={`${open ? "Collapse" : "Expand"} ${folder.name}`} aria-expanded={open} onClick={() => expansion.toggle(folder.id)} className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-zinc-500 hover:text-zinc-200"><ChevronIcon open={open} /></button>
          <Link {...dragProps(folder.id)} href={hrefFor(folder.id)} aria-current={active ? "page" : undefined} onClick={onCloseDrawer} className="flex h-9 min-w-0 flex-1 items-center gap-1.5" title={folder.name}>
            <FolderIcon className="size-3.5 shrink-0 text-zinc-500" /><span className="truncate">{folder.name}</span>
          </Link>
          <button type="button" aria-label={`Organize folder ${folder.name}`} aria-expanded={menu === folder.id} disabled={pending} onClick={() => { setMenu(menu === folder.id ? null : folder.id); setError(null); }} className={`${actionClass} opacity-60 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100`}>⋯</button>
        </div>
      </div>
      {menu === folder.id && <div className="mb-1 ml-3 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
        <button type="button" disabled={pending} className={actionClass} onClick={() => startDraft({ name: "", parentId: folder.id })}>New subfolder</button>
        <button type="button" disabled={pending} className={actionClass} onClick={() => startDraft({ id: folder.id, name: folder.name, parentId: folder.parentId })}>Rename folder</button>
        <label className="block px-2 py-1 text-xs text-zinc-500">Move to
          <select aria-label={`Move ${folder.name} to`} value={folder.parentId ?? ""} disabled={pending} onChange={event => void move(folder.id, event.target.value || null)} className="mt-1 w-full cursor-pointer rounded border border-zinc-700 bg-zinc-900 p-1.5 text-xs text-zinc-200">
            <option value="">Top level</option>
            {choices.filter(option => !containsFolder(folder.id, option.id)).map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>
        <button type="button" disabled={pending} className={`${actionClass} hover:text-red-300`} onClick={() => void remove(folder)}>Delete folder</button>
      </div>}
      {open && <ul className="space-y-0.5">
        {folder.children.map(child => folderRow(child, depth + 1))}
        {(workersByFolder.get(folder.id) ?? []).map(worker => <li key={worker.id}>{workerRow(worker, depth + 1)}</li>)}
      </ul>}
    </li>;
  }
  return <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
    <SidebarSectionHeader actions={
      <button type="button" aria-label="New folder" title="New folder" disabled={!businessId || pending} onClick={() => startDraft({ name: "", parentId: null })}
        className="mr-7 flex size-6 cursor-pointer items-center justify-center rounded text-zinc-500 transition hover:text-zinc-200 disabled:cursor-default disabled:opacity-40">
        <FolderIcon className="size-3.5" />
      </button>
    }>Outcomes</SidebarSectionHeader>
    <section aria-label="Dashboard folders" tabIndex={0} className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-lg focus-visible:outline-2 focus-visible:outline-violet-400 focus-visible:-outline-offset-2">
      {draft && <form className="mb-2 space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2" onSubmit={event => { event.preventDefault(); void saveDraft(); }}>
        <label className="block text-xs text-zinc-400">{draft.id ? "Rename folder" : draft.parentId ? `New folder in ${scopedFolders.find(folder => folder.id === draft.parentId)?.name ?? "folder"}` : "New folder"}
          <input autoFocus aria-label="Folder name" maxLength={60} required disabled={pending} value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} onKeyDown={event => { if (event.key === "Escape" && !pending) setDraft(null); }} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 outline-none focus:border-violet-500" />
        </label>
        <div className="flex justify-end gap-2 text-xs"><button type="button" disabled={pending} onClick={() => { setDraft(null); setError(null); }} className={actionClass}>Cancel</button><button type="submit" disabled={pending || !draft.name.trim()} className="cursor-pointer rounded bg-violet-600 px-2 py-1 text-white disabled:cursor-default disabled:opacity-40">Save folder</button></div>
      </form>}
      {error && <p role="alert" className="mb-2 px-3 text-xs text-red-300">{error}</p>}
      {movement.error && <p role="alert" className="mb-2 px-3 text-xs text-red-300">{movement.error}</p>}
      {movement.moving && <p role="status" className="px-3 py-1 text-xs text-zinc-400">Moving worker…</p>}
      <ul className="space-y-0.5">
        <li data-monitoring-folder-drop="root" data-drop-active={over === null} className={dropClass}
          onDragOver={event => handleDragOver(event, null)} onDragLeave={handleDragLeave} onDrop={event => handleDrop(event, null)}>
          <Link href={hrefFor(null)} onClick={onCloseDrawer} aria-current={!selectedId ? "page" : undefined} className={`flex h-9 items-center gap-1.5 rounded-lg pl-7 pr-3 text-sm ${!selectedId ? "bg-violet-500/10 font-medium text-violet-300" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100"}`}><ReportIcon className="size-3.5 shrink-0 text-zinc-500" />Entire business</Link>
        </li>
        {tree.map(folder => folderRow(folder, 0))}
      </ul>
      <div {...movement.folderProps(null)} data-worker-root="true" className={`${dropClass} ${movement.dragged ? "min-h-9" : ""}`}>
        {rootWorkers.map(worker => workerRow(worker, 0))}
      </div>
    </section>
  </div>;
}
